import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { toast } from "react-toastify";

import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import { formatSessionTime } from "../components/DeviceSession";
import "../assets/styles/page.css";
import "../assets/styles/device-sessions.css";

const PROFILE_PATH = "/admin/me";
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MIN_PASSWORD_LENGTH = 6;

const DEVICE_ICONS = {
  mobile: "pi pi-mobile",
  tablet: "pi pi-tablet",
};

const emptyPasswordForm = {
  current: "",
  next: "",
  confirm: "",
  signOutOthers: true,
};

// "Active now", "Active 12 min ago", "Active 3h ago", then a date.
const describeActivity = (value) => {
  if (!value) return "Active recently";

  const minutes = Math.floor((Date.now() - new Date(value).getTime()) / 60000);

  if (minutes < 2) return "Active now";
  if (minutes < 60) return `Active ${minutes} min ago`;
  if (minutes < 60 * 24) return `Active ${Math.floor(minutes / 60)}h ago`;

  return `Active ${new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  })}`;
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

const errorMessage = (error, fallback) => error.response?.data?.message || fallback;

const Avatar = ({ src, name, className = "account-avatar" }) => (
  <span className={className}>
    {src ? <img src={src} alt="" /> : (name || "A").charAt(0).toUpperCase()}
  </span>
);

/*
 * The signed-in admin's own account: profile details, in-place editing of
 * name, email, photo and password, and the devices they are signed in on.
 * Backed by /admin/me and /auth/sessions, which every admin can use
 * regardless of page permissions.
 */
const AdminAccount = () => {
  const { user, logout, updateUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", email: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const imageInputRef = useRef(null);

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const [passwordError, setPasswordError] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [limit, setLimit] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [confirmUid, setConfirmUid] = useState(null);
  const [busy, setBusy] = useState(false);

  /* ---------- Data ---------- */

  const applyProfile = useCallback(
    (data) => {
      if (!data) return;

      setProfile(data);
      updateUser({ name: data.name, email: data.email, image_url: data.image_url });
    },
    [updateUser]
  );

  const loadProfile = useCallback(async () => {
    try {
      const res = await api.get(PROFILE_PATH);
      applyProfile(res.data?.data);
    } catch (error) {
      toast.error(errorMessage(error, "Unable to load your profile."));
    } finally {
      setProfileLoading(false);
    }
  }, [applyProfile]);

  const loadSessions = useCallback(async () => {
    try {
      const res = await api.get("/auth/sessions");
      const data = res.data?.data || {};

      // This device first, then most recently active.
      setSessions(
        [...(data.sessions || [])].sort(
          (a, b) =>
            Number(b.is_current) - Number(a.is_current) ||
            new Date(b.last_seen_at) - new Date(a.last_seen_at)
        )
      );
      setLimit(data.limit ?? null);
    } catch (error) {
      toast.error(errorMessage(error, "Unable to load your devices."));
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
    loadSessions();
  }, [loadProfile, loadSessions]);

  // Release the preview object URL when it changes or the page unmounts.
  useEffect(
    () => () => {
      if (imagePreview.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
    },
    [imagePreview]
  );

  const account = profile || user || {};

  /* ---------- Edit profile ---------- */

  const openEditProfile = () => {
    setProfileForm({ name: account.name || "", email: account.email || "" });
    setImageFile(null);
    setImagePreview(account.image_url || "");
    setEditOpen(true);
  };

  const closeEditProfile = () => {
    if (savingProfile) return;

    setEditOpen(false);
    setImageFile(null);
    setImagePreview("");
  };

  const selectImage = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!IMAGE_TYPES.includes(file.type)) {
      toast.error("Choose a JPG, PNG or WEBP image.");
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("That image is larger than 5 MB.");
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const submitProfile = async (event) => {
    event.preventDefault();

    const name = profileForm.name.trim();
    const email = profileForm.email.trim().toLowerCase();
    const detailsChanged = name !== account.name || email !== account.email;

    if (!detailsChanged && !imageFile) {
      closeEditProfile();
      return;
    }

    try {
      setSavingProfile(true);

      if (imageFile) {
        const formData = new FormData();
        formData.append("image", imageFile);

        const res = await api.patch(`${PROFILE_PATH}/profile-image`, formData);
        applyProfile(res.data?.data);
      }

      if (detailsChanged) {
        const res = await api.put(PROFILE_PATH, { name, email });
        applyProfile(res.data?.data);
      }

      toast.success("Profile updated.");
      setEditOpen(false);
      setImageFile(null);
      setImagePreview("");
    } catch (error) {
      toast.error(errorMessage(error, "Unable to update your profile."));
    } finally {
      setSavingProfile(false);
    }
  };

  /* ---------- Change password ---------- */

  const openChangePassword = () => {
    setPasswordForm(emptyPasswordForm);
    setPasswordError("");
    setPasswordOpen(true);
  };

  const closeChangePassword = () => {
    if (savingPassword) return;

    setPasswordOpen(false);
    setPasswordForm(emptyPasswordForm);
    setPasswordError("");
  };

  const setPasswordField = (field) => (event) => {
    const value = field === "signOutOthers" ? event.target.checked : event.target.value;

    setPasswordForm((previous) => ({ ...previous, [field]: value }));
    setPasswordError("");
  };

  const submitPassword = async (event) => {
    event.preventDefault();

    if (passwordForm.next.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError("The new passwords don't match.");
      return;
    }

    try {
      setSavingPassword(true);

      await api.patch(`${PROFILE_PATH}/password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.next,
      });

      let message = "Password changed.";

      if (passwordForm.signOutOthers && sessions.some((session) => !session.is_current)) {
        try {
          await api.post("/auth/sessions/revoke-others");
          message = "Password changed and other devices signed out.";
        } catch {
          message = "Password changed, but other devices could not be signed out.";
        }

        await loadSessions();
      }

      toast.success(message);
      setPasswordOpen(false);
      setPasswordForm(emptyPasswordForm);
    } catch (error) {
      setPasswordError(errorMessage(error, "Unable to change your password."));
    } finally {
      setSavingPassword(false);
    }
  };

  /* ---------- Devices ---------- */

  const signOut = async (session) => {
    try {
      setBusy(true);
      const res = await api.delete(`/auth/sessions/${session.session_uid}`);

      if (res.data?.data?.signed_out_current) {
        await logout();
        return;
      }

      toast.success(`Signed out of ${session.device_label}.`);
      setConfirmUid(null);
      await loadSessions();
    } catch (error) {
      toast.error(errorMessage(error, "Unable to sign out that device."));
    } finally {
      setBusy(false);
    }
  };

  const signOutOthers = async () => {
    try {
      setBusy(true);
      const res = await api.post("/auth/sessions/revoke-others");
      toast.success(res.data?.message || "Other devices signed out.");
      setConfirmUid(null);
      await loadSessions();
    } catch (error) {
      toast.error(errorMessage(error, "Unable to sign out other devices."));
    } finally {
      setBusy(false);
    }
  };

  const otherCount = sessions.filter((session) => !session.is_current).length;
  const roleName = account.role_details?.role_name || "Administrator";
  const status = account.status || "active";

  const details = [
    { label: "Full name", value: account.name },
    { label: "Email", value: account.email },
    {
      label: "Role",
      value: roleName,
      hint: account.role_details?.description || "Set by an administrator with Users access.",
    },
    { label: "Status", value: <span className={`account-status ${status}`}>{status}</span> },
    { label: "Member since", value: formatDate(account.createdAt) },
    { label: "Last updated", value: formatDate(account.updatedAt) },
    {
      label: "Devices",
      value: sessionsLoading
        ? "—"
        : `${sessions.length} signed in${limit !== null ? ` of ${limit} allowed` : ""}`,
    },
  ];

  return (
    <section className="admin-page">
      <div className="page-header">
        <div>
          <p>Account</p>
          <h1>My account</h1>
          <span>Your profile, password and the devices signed in to this account.</span>
        </div>
      </div>

      <div className="account-grid">
        <aside className="page-card account-identity">
          <button
            type="button"
            className="account-avatar-button"
            onClick={openEditProfile}
            aria-label="Change profile photo"
            title="Change profile photo"
          >
            <Avatar src={account.image_url} name={account.name} />
            <span className="account-avatar-badge" aria-hidden="true">
              <i className="pi pi-camera" />
            </span>
          </button>

          <h2>{account.name || "Admin"}</h2>
          <p>{account.email}</p>
          <span className="account-role">{roleName}</span>

          <div className="account-actions">
            <button type="button" className="primary-action" onClick={openEditProfile}>
              <i className="pi pi-user-edit" aria-hidden="true" />
              Edit profile
            </button>
            <button type="button" className="ghost-action" onClick={openChangePassword}>
              <i className="pi pi-lock" aria-hidden="true" />
              Change password
            </button>
          </div>
        </aside>

        <div className="account-main">
          <div className="page-card" aria-labelledby="account-details-heading">
            <div className="devices-card-head">
              <h2 id="account-details-heading">Account details</h2>
              <button type="button" className="devices-card-link" onClick={openEditProfile}>
                Edit
              </button>
            </div>

            {profileLoading && !profile ? (
              <div className="devices-row-skeleton account-details-skeleton" aria-label="Loading profile" />
            ) : (
              <dl className="account-details">
                {details.map((item) => (
                  <div key={item.label}>
                    <dt>{item.label}</dt>
                    <dd>
                      {item.value || "—"}
                      {item.hint && <small>{item.hint}</small>}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          <div className="page-card" aria-labelledby="account-devices-heading">
            <div className="devices-card-head">
              <h2 id="account-devices-heading">Signed-in devices</h2>

              {limit !== null && !sessionsLoading && (
                <span
                  className="devices-meter"
                  aria-label={`${sessions.length} of ${limit} devices in use`}
                >
                  <span className="devices-meter-dots" aria-hidden="true">
                    {Array.from({ length: Math.min(limit, 10) }, (_, index) => (
                      <span key={index} className={index < sessions.length ? "used" : ""} />
                    ))}
                  </span>
                  {sessions.length}/{limit}
                </span>
              )}
            </div>

            <p className="devices-card-intro">
              Where this admin account is signed in right now. Sign out anything
              you don't recognise.
            </p>

            <ul className="devices-list">
              {sessionsLoading && <li className="devices-row-skeleton" aria-label="Loading devices" />}

              {!sessionsLoading &&
                sessions.map((session) => {
                  const confirming = confirmUid === session.session_uid;
                  const sessionDetails = [
                    session.last_ip && `IP ${session.last_ip}`,
                    `Signed in ${formatSessionTime(session.created_at)}`,
                    `Expires ${formatSessionTime(session.expires_at)}`,
                  ]
                    .filter(Boolean)
                    .join(" · ");

                  return (
                    <li
                      key={session.session_uid}
                      className={`devices-row ${confirming ? "confirming" : ""}`}
                    >
                      <span className="devices-row-icon" aria-hidden="true">
                        <i className={DEVICE_ICONS[session.device_type] || "pi pi-desktop"} />
                      </span>

                      <div className="devices-row-text">
                        <div className="devices-row-name">
                          <span>{session.device_label || "Unknown device"}</span>
                          {session.is_current && (
                            <span className="devices-row-current">This device</span>
                          )}
                        </div>
                        <span className="devices-row-meta" title={sessionDetails}>
                          {confirming
                            ? session.is_current
                              ? "You'll be signed out here."
                              : "It will need to sign in again."
                            : describeActivity(session.last_seen_at)}
                        </span>
                      </div>

                      {confirming ? (
                        <div className="devices-row-confirm">
                          <button
                            type="button"
                            className="confirm-no"
                            onClick={() => setConfirmUid(null)}
                            disabled={busy}
                          >
                            Keep
                          </button>
                          <button
                            type="button"
                            className="confirm-yes"
                            onClick={() => signOut(session)}
                            disabled={busy}
                          >
                            {busy ? "..." : "Sign out"}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="devices-row-action"
                          onClick={() => setConfirmUid(session.session_uid)}
                          disabled={busy}
                          title={session.is_current ? "Sign out of this device" : "Sign out this device"}
                          aria-label={`Sign out ${session.device_label}${
                            session.is_current ? " (this device)" : ""
                          }`}
                        >
                          <i className="pi pi-sign-out" aria-hidden="true" />
                        </button>
                      )}
                    </li>
                  );
                })}
            </ul>

            <div className="devices-card-footer">
              <span>Hover a device to see its IP address and sign-in time.</span>

              {otherCount > 0 && (
                <button
                  type="button"
                  className="devices-card-link"
                  onClick={signOutOthers}
                  disabled={busy}
                >
                  Sign out other devices
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Sidebar
        visible={editOpen}
        position="right"
        onHide={closeEditProfile}
        className="premium-user-sidebar profile-edit-sidebar"
      >
        <div className="sidebar-form-shell">
          <div className="sidebar-form-header">
            <p>My account</p>
            <h2>Edit profile</h2>
            <span>Update your name, email and profile photo.</span>
          </div>

          <form className="premium-sidebar-form" onSubmit={submitProfile}>
            <div className="profile-edit-image-section">
              <div className="profile-edit-preview">
                {imagePreview ? (
                  <img src={imagePreview} alt="Profile preview" />
                ) : (
                  <div>{(profileForm.name || account.name || "A").charAt(0).toUpperCase()}</div>
                )}
              </div>

              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={() => imageInputRef.current?.click()}
                disabled={savingProfile}
              >
                <i className="pi pi-camera" aria-hidden="true" />
                {imageFile || account.image_url ? "Change photo" : "Add photo"}
              </button>

              <input
                ref={imageInputRef}
                type="file"
                accept={IMAGE_TYPES.join(",")}
                hidden
                onChange={selectImage}
              />

              <small>
                {imageFile ? `${imageFile.name} will be uploaded when you save.` : "JPG, PNG or WEBP, up to 5 MB."}
              </small>
            </div>

            <label>
              Full name
              <input
                type="text"
                value={profileForm.name}
                onChange={(event) =>
                  setProfileForm((previous) => ({ ...previous, name: event.target.value }))
                }
                autoComplete="name"
                maxLength={120}
                required
              />
            </label>

            <label>
              Email address
              <input
                type="email"
                value={profileForm.email}
                onChange={(event) =>
                  setProfileForm((previous) => ({ ...previous, email: event.target.value }))
                }
                autoComplete="email"
                required
              />
            </label>

            <div className="profile-readonly-field">
              <span>Role</span>
              <strong>
                <i className="pi pi-shield" aria-hidden="true" /> {roleName}
              </strong>
              <small>Only an administrator with Users access can change roles.</small>
            </div>

            <div className="sidebar-form-footer">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={closeEditProfile}
                disabled={savingProfile}
              >
                Cancel
              </button>
              <button type="submit" className="primary-action" disabled={savingProfile}>
                {savingProfile ? "Saving..." : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      </Sidebar>

      <Sidebar
        visible={passwordOpen}
        position="right"
        onHide={closeChangePassword}
        className="premium-user-sidebar compact-password-sidebar"
      >
        <div className="sidebar-form-shell">
          <div className="sidebar-form-header">
            <p>Account security</p>
            <h2>Change password</h2>
            <span>Confirm your current password, then choose a new one.</span>
          </div>

          <form className="premium-sidebar-form" onSubmit={submitPassword}>
            {/* Lets password managers attach the new password to this account. */}
            <input
              type="email"
              value={account.email || ""}
              autoComplete="username"
              readOnly
              hidden
            />

            <label>
              Current password
              <input
                type="password"
                value={passwordForm.current}
                onChange={setPasswordField("current")}
                autoComplete="current-password"
                required
              />
            </label>

            <label>
              New password
              <input
                type="password"
                value={passwordForm.next}
                onChange={setPasswordField("next")}
                autoComplete="new-password"
                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                minLength={MIN_PASSWORD_LENGTH}
                required
              />
            </label>

            <label>
              Confirm new password
              <input
                type="password"
                value={passwordForm.confirm}
                onChange={setPasswordField("confirm")}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                required
              />
            </label>

            {otherCount > 0 && (
              <label className="account-checkbox">
                <input
                  type="checkbox"
                  checked={passwordForm.signOutOthers}
                  onChange={setPasswordField("signOutOthers")}
                />
                Sign out my {otherCount} other {otherCount === 1 ? "device" : "devices"}
              </label>
            )}

            {passwordError && (
              <p className="account-form-error" role="alert">
                {passwordError}
              </p>
            )}

            <div className="sidebar-form-footer">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={closeChangePassword}
                disabled={savingPassword}
              >
                Cancel
              </button>
              <button type="submit" className="primary-action" disabled={savingPassword}>
                {savingPassword ? "Updating..." : "Update password"}
              </button>
            </div>
          </form>
        </div>
      </Sidebar>
    </section>
  );
};

export default AdminAccount;
